import { FC } from "react";

import { BarEvent, EventsBar } from "@nens/chakra/components";
import { Wind, WindDragCoefficient } from "@nens/threedi/openapi-client";

import { GridCard } from "../../../components/GridCard";

type Block = BarEvent & { value: number; direction: number };

function eventValuesToBlocks(
  values: number[][] | undefined,
  duration: number,
  offset: number
): Block[] {
  if (!values) return [];

  return values
    .map(([start, value, direction], idx): Block => {
      // Let each event run until the timestamp of the next change, or
      // until 'duration'.
      const realStart = start + offset;
      const end =
        idx + 1 < values.length
          ? values[idx + 1][0] + offset
          : offset + (duration ?? values[idx][0]);
      return { start: realStart, end, value, direction };
    })
    .filter(
      // Filter out empty events (especially if duration equals
      // timestamp of the last event, or is undefined)
      // Also filter out events without wind.
      (event) => event.start !== event.end && event.value > 0
    );
}

interface WindPopupContentProps {
  windEvent: Wind;
  block: Block;
  windDragCoefficient?: WindDragCoefficient;
}

const WindPopupContent: FC<WindPopupContentProps> = ({ windEvent, block, windDragCoefficient }) => {
  return (
    <>
      <p>
        Speed: {block.value} {windEvent.units}
      </p>
      <p>Direction: {block.direction}&deg;</p>
      <p>Wind drag coefficient: {windDragCoefficient ? windDragCoefficient.value : "--"}</p>
    </>
  );
};

interface WindEventsProps {
  simulationDuration: number;
  events?: Wind[];
  windDragCoefficient?: WindDragCoefficient;
}

export const WindEvents: FC<WindEventsProps> = ({
  simulationDuration,
  events,
  windDragCoefficient,
}) => {
  if (!events || !events.length) return null;

  const windEvents: BarEvent[][] = events.map((windEvent) => {
    return eventValuesToBlocks(
      windEvent.values,
      simulationDuration - windEvent.offset,
      windEvent.offset
    ).map((block) => {
      return {
        start: block.start,
        end: block.end,
        title: "Wind",
        content: (
          <WindPopupContent
            windEvent={windEvent}
            block={block}
            windDragCoefficient={windDragCoefficient}
          />
        ),
        value: Math.max(block.value, 0),
      };
    });
  });

  return (
    <GridCard variant="twocolumns" title="Wind">
      <EventsBar
        start={0}
        end={simulationDuration}
        events={windEvents.flat()}
        height="30px"
        blockColor="teal.200"
        borderColor="brand.grey.600"
      />
    </GridCard>
  );
};
